import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DateSelector from "../../../components/controls/DatePicker";
import TimeSelector from "../../../components/controls/TimeSelector";

const EventDatePicker = ({
  nextStep,
  updateEventData,
  eventData,
  goToPreviousStep,
}) => {
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState(eventData.eventDate || null);
  const [customEndDate, setCustomEndDate] = useState(
    eventData.eventEndDate || null
  );
  const [useCustomEndDate, setUseCustomEndDate] = useState(
    !!eventData.endDate && eventData.endDate !== eventData.startDate
  );

  const formatTime = (date) =>
    date
      ? new Date(date).toLocaleTimeString("fr-CA", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : "";

  const [guestArrivalTime, setGuestArrivalTime] = useState(
    formatTime(eventData.guestArrivalTime) || ""
  );
  const [eventStartTime, setEventStartTime] = useState(
    formatTime(eventData.eventDate) || ""
  );
  const [eventEndTime, setEventEndTime] = useState(
    formatTime(eventData.eventEndDate) || ""
  );

  const [errors, setErrors] = useState({});

  const combineDateAndTime = (date, time) => {
    if (!date || !time || typeof time !== "string" || !time.includes(":"))
      return null;
    const [hours, minutes] = time.split(":").map(Number);
    const combined = new Date(date);
    combined.setHours(hours);
    combined.setMinutes(minutes);
    return combined;
  };

  const calculateEndDate = () => {
    if (!startDate || !eventStartTime || !eventEndTime) return startDate;
    const [startHour, startMin] = eventStartTime.split(":").map(Number);
    const [endHour, endMin] = eventEndTime.split(":").map(Number);

    const end = new Date(startDate);
    end.setHours(endHour);
    end.setMinutes(endMin);

    if (endHour < startHour || (endHour === startHour && endMin <= startMin)) {
      end.setDate(end.getDate() + 1);
    }
    return end;
  };

  const displayedEndDate = useCustomEndDate
    ? combineDateAndTime(customEndDate, eventEndTime)
    : calculateEndDate();

  useEffect(() => {
    const eventStartDateTime = combineDateAndTime(startDate, eventStartTime);
    const eventEndDateTime = displayedEndDate;
    const guestArrivalDateTime = combineDateAndTime(
      startDate,
      guestArrivalTime
    );

    updateEventData({
      eventDate: eventStartDateTime, // ✅ début
      eventEndDate: eventEndDateTime, // ✅ fin
      guestArrivalTime: guestArrivalDateTime, // ✅ arrivée
    });
  }, [
    startDate,
    customEndDate,
    guestArrivalTime,
    eventStartTime,
    eventEndTime,
    useCustomEndDate,
  ]);

  const handleSubmit = () => {
    if (!startDate || !eventStartTime || !eventEndTime) {
      setErrors({ dateRange: t("errors.eventDateAndTime") });
      return;
    }
    nextStep();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm w-full max-w-lg overflow-hidden border border-gray-100">
      <div className="bg-gradient-to-b from-[#f0f9ff] to-white">
        <h2 className="text-xl font-bold text-center text-gray-800 pt-4">
          {t("stepTwo.title")}
        </h2>
      </div>

      <div className="py-4 text-text">
        <div className="form-control w-full px-6">
          <DateSelector
            label={t("calendar.startDate")}
            value={startDate}
            onChange={setStartDate}
          />

          <div className="mt-6 space-y-4">
            <TimeSelector
              label={t("calendar.guestArrival")}
              value={guestArrivalTime}
              onChange={setGuestArrivalTime}
            />
            <TimeSelector
              label={t("calendar.eventStart")}
              value={eventStartTime}
              onChange={setEventStartTime}
              required
            />
            <TimeSelector
              label={t("calendar.eventEnd")}
              value={eventEndTime}
              onChange={setEventEndTime}
              required
            />
          </div>

          {!useCustomEndDate && startDate && eventStartTime && eventEndTime && (
            <p className="mt-4 text-sm text-center text-gray-600">
              {t("calendar.automaticallyEnds")}{" "}
              {calculateEndDate().toLocaleDateString()}
            </p>
          )}

          <button
            onClick={() => setUseCustomEndDate(!useCustomEndDate)}
            className="mt-2 text-sm text-primary underline mx-auto block"
          >
            {useCustomEndDate
              ? t("calendar.cancelCustomEndDate")
              : t("calendar.setDifferentEndDate")}
          </button>

          {useCustomEndDate && (
            <div className="mt-4">
              <DateSelector
                label={t("calendar.customEndDate")}
                value={customEndDate}
                onChange={setCustomEndDate}
              />
            </div>
          )}

          {errors.dateRange && (
            <p className="text-red-500 text-sm text-center mt-4">
              {errors.dateRange}
            </p>
          )}

          <div className="flex justify-between gap-x-4 p-6">
            <button
              onClick={goToPreviousStep}
              className="w-1/2 bg-muted text-white py-3 rounded-full hover:bg-gray-300 transition focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
            >
              {t("stepTwo.buttons.back")}
            </button>

            <button
              onClick={handleSubmit}
              className="w-1/2 bg-primary text-white py-3 rounded-full hover:bg-primary/90 transition focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              {t("stepOne.nextButton")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDatePicker;
